/* ==============================================================================
   Layout do aplicativo
   ==============================================================================
   A diferença ESTRUTURAL em relação ao Discord, e a razão dela:

   O Discord usa colunas encostadas, de borda a borda, separadas por uma linha de
   1px. Aqui os painéis FLUTUAM com respiro entre eles, e o fundo aparece nas
   frestas. Isso muda a silhueta da tela inteira em vez de apenas a paleta — que
   é o que faz "parecer outro produto" em vez de "Discord com outra cor".

   Consequência técnica: os painéis precisam de raio e sombra próprios, e o
   respiro tem de ser um token, senão cada painel escolhe o seu e a grade
   desalinha.

   Painéis redimensionáveis por arraste, com a largura persistida. Três detalhes
   que separam um resize utilizável de um irritante:

     1. **O ponteiro é capturado** (`setPointerCapture`). Sem isso, arrastar
        rápido faz o cursor sair do elemento e o arraste morre no meio.
     2. **A seleção de texto é bloqueada durante o arraste.** Sem isso, o
        movimento seleciona a interface inteira em azul.
     3. **A largura é limitada nos dois extremos.** Um painel arrastado a 8px
        fica inalcançável para arrastar de volta.
   ============================================================================== */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface AppLayoutProps {
  /** Barra superior: contexto atual à esquerda, usuário à direita. */
  topBar: ReactNode;
  /** Painel esquerdo: canais, ou amigos e conversas. */
  sidebar: ReactNode;
  /** Área principal. */
  children: ReactNode;
  /** Painel direito: membros do servidor, ou chat durante a chamada. */
  aside?: ReactNode;
  /** Barra de controles de voz. Aparece só em chamada. */
  voiceBar?: ReactNode;
  /** Dock de servidores, posicionada por ela mesma. */
  dock: ReactNode;

  sidebarCollapsed?: boolean;
  asideCollapsed?: boolean;
  onToggleAside?: () => void;
}

/* ------------------------------------------------------------------------------
   Responsividade
   ------------------------------------------------------------------------------
   O layout tem três painéis lado a lado. Numa janela de 900px isso deixa a área
   de mensagens com ~300px, que é estreito demais para conversa — e foi a
   observação de "sem responsividade".

   A regra: painéis somem por PRIORIDADE, do menos essencial ao mais.

     abaixo de 1100px   a lista de membros sai (é consulta, não conversa)
     abaixo de 820px    a sidebar recolhe para ícones
     abaixo de 640px    a sidebar sai por completo; volta como sobreposição

   Medido em `window.innerWidth` e não em media query de CSS porque a decisão
   muda o que é RENDERIZADO, não só o estilo — um painel escondido por CSS
   continua montando componentes e assinando eventos.
------------------------------------------------------------------------------ */
const BREAKPOINT_ASIDE = 1100;
const BREAKPOINT_SIDEBAR_ICONS = 820;
const BREAKPOINT_SIDEBAR_HIDDEN = 640;

/** Limites de arraste. Fora deles o painel fica inutilizável. */
const SIDEBAR_MIN = 180;
const SIDEBAR_MAX = 380;
const ASIDE_MIN = 200;
const ASIDE_MAX = 420;

const STORAGE_KEY = 'concord.layout.v1';

interface StoredLayout {
  sidebar: number;
  aside: number;
}

/**
 * Larguras persistidas.
 *
 * `try/catch` porque localStorage lança em modo privado de alguns navegadores e
 * quando a cota está cheia — e o layout não deve impedir o app de abrir.
 */
function loadLayout(): StoredLayout {
  const fallback: StoredLayout = { sidebar: 236, aside: 240 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<StoredLayout>;
    return {
      // Clampa ao carregar: um valor salvo por uma versão anterior com outros
      // limites deixaria o painel fora da faixa utilizável.
      sidebar: clamp(parsed.sidebar ?? fallback.sidebar, SIDEBAR_MIN, SIDEBAR_MAX),
      aside: clamp(parsed.aside ?? fallback.aside, ASIDE_MIN, ASIDE_MAX),
    };
  } catch {
    return fallback;
  }
}

function saveLayout(layout: StoredLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // Persistir largura de painel não vale interromper nada.
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Largura da janela, com o valor já classificado em decisões de layout.
 *
 * Devolve BOOLEANOS e não o número: quem consome não precisa saber os limites, e
 * espalhar comparações de pixel pelo componente é como uma delas fica com o
 * número errado depois de um ajuste.
 *
 * `ResizeObserver` no `<html>` em vez do evento `resize` da janela: o evento
 * dispara em situações que não mudam o layout — abrir o teclado virtual no
 * celular é a mais comum — e cada disparo custaria um render da árvore inteira.
 */
function useViewport(): {
  hideAside: boolean;
  iconsOnlySidebar: boolean;
  hideSidebar: boolean;
} {
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth,
  );

  useEffect(() => {
    const element = document.documentElement;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === 'number') setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return {
    hideAside: width < BREAKPOINT_ASIDE,
    iconsOnlySidebar:
      width < BREAKPOINT_SIDEBAR_ICONS && width >= BREAKPOINT_SIDEBAR_HIDDEN,
    hideSidebar: width < BREAKPOINT_SIDEBAR_HIDDEN,
  };
}

export function AppLayout({
  topBar,
  sidebar,
  children,
  aside,
  voiceBar,
  dock,
  sidebarCollapsed = false,
  asideCollapsed = false,
}: AppLayoutProps) {
  const [layout, setLayout] = useState<StoredLayout>(loadLayout);
  const dragging = useRef<'sidebar' | 'aside' | null>(null);
  const viewport = useViewport();

  /*
   * Em janela estreita a sidebar sai do fluxo e volta como SOBREPOSIÇÃO.
   *
   * Sem isso, a única forma de alcançar os canais numa tela pequena seria
   * alargar a janela — e num celular isso não existe. A sobreposição é fechada
   * ao escolher um canal, que é o gesto que a pessoa já ia fazer.
   */
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fecha a gaveta quando a janela cresce: deixá-la aberta cobriria o conteúdo
  // que acabou de ganhar espaço.
  useEffect(() => {
    if (!viewport.hideSidebar) setDrawerOpen(false);
  }, [viewport.hideSidebar]);

  useEffect(() => {
    saveLayout(layout);
  }, [layout]);

  const startDrag = useCallback(
    (which: 'sidebar' | 'aside') => (event: React.PointerEvent<HTMLDivElement>) => {
      dragging.current = which;
      // Captura o ponteiro: sem isso, arrastar rápido leva o cursor fora do
      // elemento e o arraste morre no meio do movimento.
      event.currentTarget.setPointerCapture(event.pointerId);
      // Bloqueia seleção durante o arraste, senão o movimento pinta a
      // interface inteira de azul.
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    [],
  );

  const onDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const which = dragging.current;
    if (!which) return;

    setLayout((current) => {
      if (which === 'sidebar') {
        return {
          ...current,
          sidebar: clamp(event.clientX - GUTTER, SIDEBAR_MIN, SIDEBAR_MAX),
        };
      }
      return {
        ...current,
        aside: clamp(window.innerWidth - event.clientX - GUTTER, ASIDE_MIN, ASIDE_MAX),
      };
    });
  }, []);

  const endDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  /** Ajuste por teclado: resize precisa funcionar sem mouse. */
  const nudge = useCallback(
    (which: 'sidebar' | 'aside', delta: number) =>
      setLayout((current) =>
        which === 'sidebar'
          ? { ...current, sidebar: clamp(current.sidebar + delta, SIDEBAR_MIN, SIDEBAR_MAX) }
          : { ...current, aside: clamp(current.aside + delta, ASIDE_MIN, ASIDE_MAX) },
      ),
    [],
  );

  /*
   * A largura efetiva combina a escolha do usuário com o espaço disponível, e o
   * espaço VENCE.
   *
   * Respeitar a largura arrastada numa janela de 700px deixaria a área de
   * mensagens com 200px — tecnicamente a preferência da pessoa, e inutilizável.
   * A largura salva não é perdida: ela volta quando a janela cresce.
   */
  const sidebarWidth = viewport.iconsOnlySidebar
    ? COLLAPSED_WIDTH
    : sidebarCollapsed
      ? COLLAPSED_WIDTH
      : layout.sidebar;

  const showSidebarInline = !viewport.hideSidebar;
  const showAside = aside !== undefined && !asideCollapsed && !viewport.hideAside;
  const resizable = !viewport.iconsOnlySidebar && !sidebarCollapsed;

  return (
    <div
      /*
        A altura vem da CLASSE e não do `style`, e a razão é técnica.
        Ver `.concord-shell` no bloco `<style>` logo abaixo: são DUAS
        declarações de `height`, e um objeto de estilo em JavaScript não
        consegue ter a mesma chave duas vezes — a segunda apagaria a primeira e
        o fallback não existiria.
      */
      className="ambient concord-shell flex w-screen flex-col overflow-hidden"
      style={{
        /*
         * `.ambient` traz dois halos radiais fixos — violeta no topo-esquerda,
         * magenta no baixo-direita. É o que tira a sensação de fundo chapado
         * sem clarear a cor de base.
         *
         * Clarear a base seria a saída óbvia e a errada: achataria o contraste
         * do texto contra ela, que é justamente o que os tokens de superfície
         * protegem. O halo dá profundidade E mantém o escuro onde o texto
         * precisa.
         */
        gap: GUTTER,
        /* Lados e topo com o respiro padrão; embaixo, folga para a dock
           flutuante não cobrir conteúdo. Declarado em propriedades separadas em
           vez de `padding` + `paddingBottom`: a forma abreviada seguida da
           específica funciona, mas repetir a chave num objeto de estilo é erro
           silencioso — a segunda vence e a intenção fica ambígua na leitura. */
        paddingTop: GUTTER,
        paddingLeft: GUTTER,
        paddingRight: GUTTER,
        /*
         * A folga de baixo soma a área segura do aparelho.
         *
         * `env(safe-area-inset-bottom)` é a barra de gestos do Android e o
         * indicador de home do iPhone. Sem somá-la, a barra de comando é
         * empurrada para cima pelo sistema e passa a cobrir a última linha da
         * conversa — o mesmo sintoma que `DOCK_CLEARANCE` existe para evitar,
         * só que aparecendo apenas no celular.
         *
         * Fora do celular `env()` vale zero e a conta dá o valor de sempre.
         */
        paddingBottom: `calc(${DOCK_CLEARANCE}px + env(safe-area-inset-bottom, 0px))`,
      }}
    >
      <style>{`
        /* --------------------------------------------------------------------
           Altura da janela no celular
           --------------------------------------------------------------------
           \`100vh\` no Android é a viewport GRANDE — a altura com a barra de
           endereço escondida. Com ela visível, os últimos ~56px da página ficam
           abaixo da dobra: exatamente onde vive o campo de mensagem. Era o
           relato "campo de texto embaixo da toolbar". O campo estava lá, fora da
           parte visível, e a barra de comando — que é \`position: fixed\` e por
           isso se ancora na viewport VISÍVEL — aparecia por cima do que sobrava.

           \`100dvh\` acompanha a barra de endereço aparecendo e sumindo. Junto
           com \`interactive-widget=resizes-content\` no \`<meta viewport>\`,
           acompanha também o teclado virtual.

           As duas declarações são o fallback do CSS: onde \`dvh\` não existe, a
           segunda linha é descartada e a primeira vale.
        -------------------------------------------------------------------- */
        .concord-shell { height: 100vh; height: 100dvh; }

        @keyframes concord-drawer-in {
          from { opacity: 0; transform: translateX(-14px) }
          to   { opacity: 1; transform: none }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes concord-drawer-in { from { opacity: 0 } to { opacity: 1 } }
        }
      `}</style>

      {/* ------------------------------------------------------------------------
          Barra superior — painel próprio, como os outros
      ------------------------------------------------------------------------ */}
      <Panel className="shrink-0">{topBar}</Panel>

      {/* ------------------------------------------------------------------------
          Corpo
      ------------------------------------------------------------------------ */}
      <div className="relative flex min-h-0 flex-1" style={{ gap: GUTTER }}>
        {showSidebarInline && (
          <>
            <Panel
              style={{ width: sidebarWidth }}
              className="shrink-0 overflow-hidden"
            >
              {sidebar}
            </Panel>

            <ResizeHandle
              label="Redimensionar barra lateral"
              onPointerDown={startDrag('sidebar')}
              onPointerMove={onDrag}
              onPointerUp={endDrag}
              onNudge={(delta) => nudge('sidebar', delta)}
              value={sidebarWidth}
              min={SIDEBAR_MIN}
              max={SIDEBAR_MAX}
              disabled={!resizable}
            />
          </>
        )}

        {/* --------------------------------------------------------------------
            Gaveta em janela estreita
            --------------------------------------------------------------------
            Botão flutuante para abrir, e a sidebar entra deslizando por cima. A
            cortina fecha ao toque — o gesto que se espera de uma gaveta.
        -------------------------------------------------------------------- */}
        {!showSidebarInline && (
          <>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir canais"
              aria-expanded={drawerOpen}
              className="glass absolute grid place-items-center"
              style={{
                left: GUTTER + 8,
                bottom: DOCK_CLEARANCE + 8,
                width: 42,
                height: 42,
                borderRadius: 'var(--radius-full)',
                boxShadow: 'var(--glass-edge), var(--glow-medium)',
                color: 'var(--color-text-primary)',
                zIndex: 'var(--z-dropdown)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M3 5h12M3 9h12M3 13h8" stroke="currentColor"
                      strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>

            {drawerOpen && (
              <>
                <button
                  type="button"
                  aria-label="Fechar canais"
                  onClick={() => setDrawerOpen(false)}
                  className="fixed inset-0 cursor-default"
                  style={{
                    background: 'oklch(4% 0.02 292 / 0.55)',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    zIndex: 'var(--z-overlay)',
                  }}
                />
                <Panel
                  className="fixed overflow-hidden"
                  style={{
                    left: GUTTER,
                    top: GUTTER,
                    bottom: DOCK_CLEARANCE,
                    width: Math.min(300, SIDEBAR_MAX),
                    zIndex: 'var(--z-overlay)',
                    boxShadow:
                      'var(--glass-edge), 0 16px 48px oklch(0% 0 0 / 0.55), var(--glow-soft)',
                    animation:
                      'concord-drawer-in var(--duration-normal) var(--ease-out-expo) both',
                  }}
                  /* Fecha ao escolher um canal: o clique já expressa a intenção
                     de sair da gaveta, e exigir um segundo toque irrita. */
                  onClickCapture={() => setDrawerOpen(false)}
                >
                  {sidebar}
                </Panel>
              </>
            )}
          </>
        )}

        <Panel className="min-w-0 flex-1 overflow-hidden">
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
            {/* A barra de voz mora DENTRO do painel principal, não no rodapé da
                janela: ela pertence à chamada em curso, e no rodapé competiria
                com a dock por atenção e por espaço. */}
            {voiceBar && (
              <div
                className="shrink-0"
                style={{
                  /*
                   * Aresta de luz em vez de `border-top`.
                   *
                   * A barra de voz é um bloco DENTRO do painel principal. Uma
                   * linha sólida ali corta o painel em dois e desfaz a sensação
                   * de peça única. O reflexo na aresta separa sem cortar.
                   */
                  boxShadow: 'inset 0 1px 0 oklch(100% 0 0 / 0.07)',
                  background: 'oklch(0% 0 0 / 0.18)',
                }}
              >
                {voiceBar}
              </div>
            )}
          </div>
        </Panel>

        {showAside && (
          <>
            <ResizeHandle
              label="Redimensionar painel lateral direito"
              onPointerDown={startDrag('aside')}
              onPointerMove={onDrag}
              onPointerUp={endDrag}
              onNudge={(delta) => nudge('aside', -delta)}
              value={layout.aside}
              min={ASIDE_MIN}
              max={ASIDE_MAX}
            />
            <Panel style={{ width: layout.aside }} className="shrink-0 overflow-hidden">
              {aside}
            </Panel>
          </>
        )}
      </div>

      {dock}
    </div>
  );
}

/** Respiro entre painéis. É o token que faz a grade alinhar. */
const GUTTER = 8;
/**
 * Altura reservada embaixo para a barra de comando flutuante.
 *
 * Não é um número escolhido: é a soma da geometria da cápsula. 8 de recuo
 * interno em cima, 52 de altura de botão com rótulo, 8 embaixo, mais 12 de
 * distância da borda da janela = 80, e 8 de folga para o painel não encostar na
 * barra. Ver `--layout-commandbar` em theme.css, que carrega o mesmo valor para
 * quem precisar dele em CSS.
 *
 * Errar aqui não produz erro nenhum — produz a barra cobrindo a última linha da
 * conversa, que foi o sintoma quando os controles de chamada ganharam rótulo e
 * a cápsula cresceu 20px sem esta constante acompanhar.
 */
const DOCK_CLEARANCE = 88;
/** Largura da barra lateral recolhida: só ícones. */
const COLLAPSED_WIDTH = 52;

/**
 * Painel flutuante.
 *
 * Raio e sombra próprios porque cada painel é um objeto separado — é justamente
 * isso que diferencia a silhueta das colunas encostadas do Discord.
 */
function Panel({
  children,
  className = '',
  style,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /*
   * Repassa o resto das props de `div`.
   *
   * Necessário porque a gaveta precisa de `onClickCapture` para fechar ao
   * escolher um canal. A alternativa — uma prop `onClickCapture` explícita —
   * resolveria hoje e voltaria na próxima prop de DOM que faltar. Um tipo que
   * enumera atributos de HTML um a um envelhece a cada uso novo.
   */
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'className' | 'style'>) {
  return (
    <div
      {...rest}
      className={className}
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-xl)',
        /*
         * A borda de 1px sólida saiu, e é a mudança visual de maior efeito
         * aqui.
         *
         * Num layout de painéis flutuantes, cada painel com moldura desenha uma
         * linha — e cinco linhas juntas leem como uma GRADE, não como cinco
         * peças. Era o "contorno horrível".
         *
         * No lugar: `--glass-edge` (reflexo na aresta de cima, sombra na de
         * baixo, o que material translúcido faz de verdade) mais uma sombra
         * projetada que separa o painel do fundo por PROFUNDIDADE em vez de por
         * traço.
         */
        boxShadow: 'var(--glass-edge), 0 8px 32px oklch(0% 0 0 / 0.32)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Alça de redimensionamento.
 *
 * `role="separator"` com `aria-valuenow` é o papel correto: leitor de tela
 * anuncia como divisor ajustável, e as setas funcionam. Uma `div` com
 * `onPointerDown` seria invisível para teclado — e o usuário pediu explicitamente
 * poder aumentar e diminuir.
 */
function ResizeHandle({
  label,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onNudge,
  value,
  min,
  max,
  disabled = false,
}: {
  label: string;
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  onNudge: (delta: number) => void;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
}) {
  const [active, setActive] = useState(false);

  if (disabled) return null;

  return (
    <div
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={(event) => {
        setActive(true);
        onPointerDown(event);
      }}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => {
        setActive(false);
        onPointerUp(event);
      }}
      onKeyDown={(event) => {
        // Passo de 16px com a seta, 64px com Shift: ajuste fino e grosso sem
        // precisar de mouse.
        const step = event.shiftKey ? 64 : 16;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          onNudge(-step);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          onNudge(step);
        }
      }}
      className="group relative shrink-0 cursor-col-resize"
      style={{ width: 4 }}
    >
      {/*
        Área de acerto MAIOR que o traço visível.
        4px de alvo é frustrante de acertar; 12px de área com 2px de traço dá
        precisão sem engordar a interface. É o mesmo princípio do alvo mínimo de
        toque, aplicado ao mouse.
      */}
      <span
        aria-hidden="true"
        className="absolute inset-y-0"
        style={{ left: -4, right: -4 }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-1/2 -translate-x-1/2 transition-colors"
        style={{
          width: 2,
          borderRadius: 999,
          background: active ? 'var(--color-accent)' : 'transparent',
          transitionDuration: 'var(--duration-fast)',
        }}
      />
    </div>
  );
}
